import { formatShortDateWithWeekday, formatTime } from "@clipboard-health/date-time";
import { Stack } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { HourlyPayLabel } from "@src/appV2/redesign/Pay/HourlyPayLabel";
import { ShiftCardWrapper } from "@src/appV2/redesign/Shift/Card/Wrapper";
import { BookingCardContentWrapper } from "@src/appV2/redesign/Shift/CardContentWrapper";
import { InstantPayPill } from "@src/appV2/redesign/Shift/InstantPayPill";
import { ShiftCardPillsWrapper } from "@src/appV2/redesign/Shift/PillsWrapper";
import { BookingCardSummaryWrapper } from "@src/appV2/redesign/Shift/SummaryWrapper";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { parseISO } from "date-fns";

import { DurationWithBreakDisplay } from "../../components/DurationWithBreakDisplay";
import { MINIMUM_TIME_FOR_MANDATORY_BREAK_IN_HOURS } from "../../Shift/constants";

interface ShiftBlockShiftCardProps {
  shift: {
    id: string;
    start: string;
    end: string;
    time: number;
    pay: number;
    isInstantPay: boolean;
    offer?: { pay?: { value: number } } | null;
  };
  agentTimezone: string;
  mandatoryBreakDurationInMinutes: number;
}

export function ShiftBlockShiftCard(props: ShiftBlockShiftCardProps) {
  const { shift, agentTimezone, mandatoryBreakDurationInMinutes } = props;

  const formattedHourlyPay = formatDollarsAsUsd(shift.offer?.pay?.value ?? shift.pay, {
    minimumFractionDigits: 2,
  });

  return (
    <ShiftCardWrapper sx={{ minHeight: "auto" }}>
      <BookingCardContentWrapper sx={{ gap: 2 }}>
        <BookingCardSummaryWrapper sx={{ justifyContent: "space-between" }}>
          <ShiftCardPillsWrapper>{shift.isInstantPay && <InstantPayPill />}</ShiftCardPillsWrapper>

          <HourlyPayLabel hourlyPay={formattedHourlyPay} size="small" />
        </BookingCardSummaryWrapper>

        <Stack gap={1}>
          <Text variant="body2">
            {formatShortDateWithWeekday(parseISO(shift.start), { timeZone: agentTimezone })}
          </Text>

          <Stack gap={0.5}>
            <Text semibold variant="body1">
              {formatTime(parseISO(shift.start), { timeZone: agentTimezone })} -{" "}
              {formatTime(parseISO(shift.end), { timeZone: agentTimezone })}
            </Text>
            <DurationWithBreakDisplay
              totalDurationInHours={shift.time}
              breakDurationInMinutes={mandatoryBreakDurationInMinutes}
              requiresBreak={
                shift.time >= MINIMUM_TIME_FOR_MANDATORY_BREAK_IN_HOURS &&
                mandatoryBreakDurationInMinutes > 0
              }
            />
          </Stack>
        </Stack>
      </BookingCardContentWrapper>
    </ShiftCardWrapper>
  );
}
